import { View } from "@novorender/api";

import type { AppDispatch, RootState } from "app/store";
import { undoRedoActions } from "features/undoRedo";

import { PointLine, pointLineActions, PointLinePoint, selectPointLines } from "./pointLineSlice";

/**
 * Builds undo/redo for creating the first segment of a point-to-point measurement
 * (i.e. when the second point is added, going from 1 to 2 points).
 *
 * Undo:  deletes the whole line.
 * Redo:  re-adds both points with their original IDs.
 */
export function createCreateLineUndoRedo(
    dispatch: AppDispatch,
    view: View,
    pointLine: Pick<PointLine, "id" | "points">,
) {
    const lineId = pointLine.id;
    const firstPt = pointLine.points[0];
    const secondPt = pointLine.points[1];
    return {
        undoCallback: () => {
            dispatch(pointLineActions.deletePointlineById(lineId));
            dispatch(pointLineActions.newPointLine());
        },
        redoCallback: () => {
            dispatch(
                pointLineActions.addPoint(
                    { position: firstPt.position, snap: firstPt.snap, objectId: firstPt.objectId ?? -1 },
                    view,
                    firstPt.id,
                    lineId,
                ),
            );
            dispatch(
                pointLineActions.addPoint(
                    { position: secondPt.position, snap: secondPt.snap, objectId: secondPt.objectId ?? -1 },
                    view,
                    secondPt.id,
                    lineId,
                ),
            );
        },
    };
}

/**
 * Builds undo/redo for adding a subsequent point to an existing line
 * (i.e. when 3+ points already exist, or extending a line).
 *
 * Undo:  deletes the added point by ID.
 * Redo:  re-adds the point with its original ID.
 */
export function createExtendLineUndoRedo(
    dispatch: AppDispatch,
    view: View,
    pointLineId: string,
    addedPoint: PointLinePoint,
    closesLine: boolean = false,
    isLineReversed: boolean = false,
) {
    return {
        undoCallback: () => dispatch(pointLineActions.deletePointById({ pointLineId, pointId: addedPoint.id }, view)),
        redoCallback: () => {
            dispatch(
                pointLineActions.addPoint(
                    { position: addedPoint.position, snap: addedPoint.snap, objectId: addedPoint.objectId ?? -1 },
                    view,
                    addedPoint.id,
                    pointLineId,
                    isLineReversed,
                ),
            );
            if (closesLine) {
                dispatch(pointLineActions.newPointLine());
            }
        },
    };
}

/**
 * Builds undo/redo for deleting an entire point-to-point line.
 *
 * Undo:  restores the line from the stored copy.
 * Redo:  deletes the line again.
 */
export function createDeleteLineUndoRedo(dispatch: AppDispatch, lineCopy: PointLine) {
    return {
        undoCallback: () => dispatch(pointLineActions.restorePointline(lineCopy)),
        redoCallback: () => dispatch(pointLineActions.deletePointlineById(lineCopy.id)),
    };
}

/**
 * Builds undo/redo for deleting a single point from a line (when the line has 3+ points).
 *
 * Undo:  re-inserts the point at its original index.
 * Redo:  deletes the point by ID again.
 */
export function createDeletePointUndoRedo(
    dispatch: AppDispatch,
    view: View,
    pointLineId: string,
    pointCopy: PointLinePoint,
    pointIdx: number,
    wasLineReversed: boolean = false,
) {
    return {
        undoCallback: () =>
            dispatch(pointLineActions.insertPoint({ pointLineId, pointIdx, point: pointCopy, wasLineReversed }, view)),
        redoCallback: () => dispatch(pointLineActions.deletePointById({ pointLineId, pointId: pointCopy.id }, view)),
    };
}

/**
 * Builds undo/redo for moving (dragging) a point.
 * Resolves indices by ID at callback execution time so that reversals or
 * other reorderings do not cause the wrong point to be targeted.
 *
 * Undo:  moves the point back to `prevPoint`.
 * Redo:  moves the point forward to `newPoint`.
 */
export function createMovePointUndoRedo(
    dispatch: AppDispatch,
    getState: () => RootState,
    view: View,
    pointLineId: string,
    pointId: string,
    prevPoint: PointLinePoint,
    newPoint: PointLinePoint,
) {
    const applyPoint = (point: PointLinePoint) => {
        const currentLines = selectPointLines(getState());
        const lidx = currentLines.findIndex((l) => l.id === pointLineId);
        const line = lidx >= 0 ? currentLines[lidx] : undefined;
        const pidx = line?.points.findIndex((p) => p.id === pointId) ?? -1;
        if (lidx >= 0 && pidx >= 0) {
            dispatch(pointLineActions.movePoint({ point, lineIdx: lidx, pointIdx: pidx }, view));
        }
    };
    return {
        undoCallback: () => applyPoint(prevPoint),
        redoCallback: () => applyPoint(newPoint),
    };
}

// Convenience wrappers that also dispatch the push to the undo/redo stack.

export function pushCreateLineUndoRedo(dispatch: AppDispatch, view: View, pointLine: Pick<PointLine, "id" | "points">) {
    dispatch(
        undoRedoActions.push({
            description: "Create point-to-point measurement",
            ...createCreateLineUndoRedo(dispatch, view, pointLine),
        }),
    );
}

export function pushExtendLineUndoRedo(
    dispatch: AppDispatch,
    view: View,
    pointLineId: string,
    addedPoint: PointLinePoint,
    closesLine: boolean,
    isLineReversed: boolean = false,
) {
    dispatch(
        undoRedoActions.push({
            description: "Add point to measurement",
            ...createExtendLineUndoRedo(dispatch, view, pointLineId, addedPoint, closesLine, isLineReversed),
        }),
    );
}

export function pushDeleteLineUndoRedo(dispatch: AppDispatch, lineCopy: PointLine) {
    dispatch(
        undoRedoActions.push({
            description: "Remove point-to-point measurement",
            ...createDeleteLineUndoRedo(dispatch, lineCopy),
        }),
    );
}

export function pushDeletePointUndoRedo(
    dispatch: AppDispatch,
    view: View,
    pointLineId: string,
    pointCopy: PointLinePoint,
    pointIdx: number,
    wasLineReversed: boolean = false,
) {
    dispatch(
        undoRedoActions.push({
            description: "Remove point from measurement",
            ...createDeletePointUndoRedo(dispatch, view, pointLineId, pointCopy, pointIdx, wasLineReversed),
        }),
    );
}

export function pushMovePointUndoRedo(
    dispatch: AppDispatch,
    getState: () => RootState,
    view: View,
    pointLineId: string,
    pointId: string,
    prevPoint: PointLinePoint,
    newPoint: PointLinePoint,
) {
    dispatch(
        undoRedoActions.push({
            description: "Move point",
            ...createMovePointUndoRedo(dispatch, getState, view, pointLineId, pointId, prevPoint, newPoint),
        }),
    );
}
